import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import {
  Smartphone, ArrowRight, LayoutGrid, Zap, Images, Clock, Mail,
  UtensilsCrossed, Check, Wallet, Ticket, Gift, Star, QrCode,
  Share2, Navigation, Contact, HelpCircle, Store, Trophy, Building2,
  ExternalLink, Scissors, Landmark,
} from "lucide-react";

const ROBBIE = "/img/mascots/robbie-winkt.webp";
const STUDIO = "https://dimedial.studio";
const CTA = "/kunden/registrieren";

/* Die echten Kundenseiten – als Live-Vorschau eingebunden */
const REAL_PAGES = [
  {
    handle: "tscheche",
    img: "/img/mini/tscheche.jpg",
    name: "Schuhhaus Tscheche",
    Icon: Store,
    kind: "Einzelhandel · Herford",
    highlight: "Homepage, Anfahrt, Kundenkarte & Newsletter",
  },
  {
    handle: "denroycadeau",
    img: "/img/mini/denroycadeau.jpg",
    name: "Denroy Cadeau",
    Icon: Scissors,
    kind: "Friseur-Salon",
    highlight: "Newsletter direkt angebunden + Galerie",
    star: "Newsletter angebunden",
  },
  {
    handle: "cdu.hoevelhof",
    img: "/img/mini/cdu.jpg",
    name: "CDU Hövelhof",
    Icon: Landmark,
    kind: "Organisation · Verein",
    highlight: "Viele Links, Social-Media & eigene Farben",
  },
];

/* Module – oben als Chips benannt, unten ausführlich */
const MODULE = [
  { Icon: LayoutGrid, label: "Links" },
  { Icon: Images, label: "Galerie" },
  { Icon: Clock, label: "Öffnungszeiten" },
  { Icon: Navigation, label: "Anfahrt & Route" },
  { Icon: Contact, label: "Visitenkarte" },
  { Icon: Mail, label: "Newsletter" },
  { Icon: Wallet, label: "Kundenkarte" },
  { Icon: Ticket, label: "Tickets & Countdown" },
  { Icon: Gift, label: "Gutschein" },
  { Icon: Star, label: "Google-Bewertungen" },
  { Icon: HelpCircle, label: "FAQ" },
  { Icon: QrCode, label: "Teilen & QR-Code" },
];

/* Module ausführlich */
const MODULE_DETAIL = [
  { Icon: Clock, title: "Öffnungszeiten", text: "Live-Status „Jetzt geöffnet“ mit Zeiten.", live: true },
  { Icon: Navigation, title: "Anfahrt & Route", text: "Ein Tipp öffnet die Karte." },
  { Icon: Contact, title: "Visitenkarte", text: "Kontakt direkt ins Handy speichern." },
  { Icon: Mail, title: "Newsletter", text: "Anmeldung landet in deiner Liste." },
  { Icon: Wallet, title: "Kundenkarte", text: "In Apple & Google Wallet." },
  { Icon: Ticket, title: "Tickets & Countdown", text: "Zähler bis zum Verkaufsstart." },
  { Icon: Gift, title: "Gutschein", text: "Verschenken & einlösen." },
  { Icon: Star, title: "Google-Bewertungen", text: "Sterne direkt auf der Seite." },
  { Icon: Images, title: "Galerie", text: "Fotos von Laden, Team & Produkten." },
  { Icon: QrCode, title: "Teilen & QR", text: "Link & QR-Code zum Aushängen." },
];

const BRANCHEN = [
  { Icon: UtensilsCrossed, name: "Gastro", text: "Speisekarte, Öffnungszeiten, Tisch-Reservierung und Live-Status." },
  { Icon: Store, name: "Handel", text: "Aktuelle Angebote, Gutschein, Kundenkarte in der Wallet und Anfahrt." },
  { Icon: Trophy, name: "Verein", text: "Termine, Mitglied-werden-Formular, Spenden-Link und Foto-Galerie." },
  { Icon: Building2, name: "Firma", text: "Leistungen, Team-Visitenkarte, Kontakt und Google-Bewertungen." },
];

const STEPS = [
  { n: "1", title: "Seite anlegen", text: "Name, Foto und einen kurzen Text eintippen – deine Mini-Website steht in 2 Minuten." },
  { n: "2", title: "Module wählen", text: "Links, Galerie, Öffnungszeiten, Kontakt oder Newsletter per Fingertipp einsetzen und sortieren." },
  { n: "3", title: "Teilen", text: "Deinen Link DIMEDIAL.studio/@name in die Instagram-Bio, den WhatsApp-Status oder auf die Visitenkarte setzen." },
];

const VERKNUEPFT = [
  { Icon: Mail, label: "Newsletter-Liste" },
  { Icon: Wallet, label: "Kundenkarte" },
  { Icon: Ticket, label: "Tickets" },
  { Icon: Share2, label: "Social Media" },
];

const FAQ = [
  { q: "Ist eine Mini-Website wirklich kostenlos?", a: "Ja. Deine erste Mini-Website ist für immer kostenlos – ohne Zahlungsdaten, ohne Zeitlimit. Du bekommst deine eigene Adresse DIMEDIAL.studio/@name mit Links, Galerie, Öffnungszeiten und Newsletter. Mehr Seiten gibt es in den Paketen (START 5, PLUS 10, TEAM 50)." },
  { q: "Was ist eine Mini-Website?", a: "Eine kompakte Seite unter deiner eigenen Adresse mit allen wichtigen Infos: Links, Öffnungszeiten, Speisekarte, Galerie und Newsletter-Anmeldung – perfekt für die Instagram-Bio oder den WhatsApp-Status." },
  { q: "Brauche ich technisches Wissen?", a: "Nein. Du tippst Texte ein, wählst Module aus und ziehst sie in die richtige Reihenfolge – alles vom Handy. Oder du schreibst Robbie per WhatsApp. Änderungen sind sofort online." },
];

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(m.matches);
    on(); m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return reduce;
};

function Reveal({ children, delay = 0, className, testId }) {
  const reduce = usePrefersReducedMotion();
  return (
    <motion.div
      className={className}
      data-testid={testId}
      initial={reduce ? false : { opacity: 0, y: 22 }}
      whileInView={reduce ? {} : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* Echte, live eingebundene Kundenseite in einem Handy-Rahmen. */
const FRAME_W = 230;
const FRAME_H = 430;
function LivePage({ page }) {
  const { handle, name, img, Icon, kind, highlight, star } = page;
  const url = `${STUDIO}/@${handle}`;
  return (
    <figure className="flex w-[230px] shrink-0 flex-col items-center" data-testid={`mw-live-${handle}`}>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="group relative block"
        style={{ width: FRAME_W }}
        aria-label={`${name} live ansehen`}
        data-testid={`mw-live-link-${handle}`}
      >
        {star && (
          <span className="absolute -right-2 -top-3 z-20 rotate-[4deg] rounded-full border-2 border-[#111] bg-[#1F7A43] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-[3px_3px_0_#111]" data-testid={`mw-live-star-${handle}`}>
            <Check size={11} strokeWidth={3} className="mr-0.5 inline" />{star}
          </span>
        )}
        <div className="overflow-hidden rounded-[2.1rem] border-[3px] border-[#111] bg-white shadow-[10px_10px_0_#111] transition-transform group-hover:-translate-y-1">
          <div className="relative flex items-center justify-center bg-[#111] py-1.5">
            <span className="h-1 w-10 rounded-full bg-white/25" aria-hidden="true" />
          </div>
          <div className="relative overflow-hidden bg-[#FBFAF5]" style={{ height: FRAME_H }}>
            <img src={img} alt={`Mini-Website von ${name}`} loading="lazy" width="230" className="w-full object-cover object-top" style={{ minHeight: FRAME_H }} />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#FBFAF5] via-[#FBFAF5]/60 to-transparent" aria-hidden="true" />
            <span className="absolute inset-x-0 bottom-3 flex justify-center">
              <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#111] bg-white px-3 py-1.5 text-[11px] font-bold uppercase text-[#111] shadow-[3px_3px_0_#111] transition-transform group-hover:scale-105">
                Live ansehen <ExternalLink size={12} />
              </span>
            </span>
          </div>
        </div>
      </a>
      <figcaption className="mt-4 w-full text-center">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#1F7A43]">
          <span className="h-2 w-2 rounded-full bg-[#1F7A43]" /> Echte Kundenseite
        </span>
        <h3 className="mt-1 flex items-center justify-center gap-1.5 font-display text-base font-extrabold uppercase leading-tight text-[#111]">
          <Icon size={15} /> {name}
        </h3>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9A958B]">{kind}</p>
        <p className="mt-1.5 text-[12.5px] leading-snug text-[#555]">{highlight}</p>
      </figcaption>
    </figure>
  );
}

/* Kleiner HTML/CSS-WhatsApp-Chat mit Robbie */
function RobbieChat() {
  return (
    <div className="mx-auto w-full max-w-[340px] overflow-hidden rounded-[2rem] border-[3px] border-[#111] bg-[#ECE5DD] shadow-[10px_10px_0_#111]" data-testid="mw-robbie-chat">
      <div className="flex items-center gap-2.5 bg-[#075E54] px-4 py-3">
        <img src={ROBBIE} alt="Robbie" width="36" height="36" className="h-9 w-9 rounded-full bg-[#FDD105] object-cover" />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-white">Robbie · DIMEDIAL</p>
          <p className="text-[10.5px] text-white/70">online</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 px-4 py-4">
        <div className="max-w-[88%] self-end rounded-2xl rounded-br-md bg-[#DCF8C6] px-3 py-2 text-[12.5px] leading-snug text-[#111] shadow-sm">
          Robbie, trag ein: Heute 14–18 Uhr geöffnet
          <span className="mt-1 block text-right text-[10px] text-[#667781]">14:02 ✓✓</span>
        </div>
        <div className="max-w-[88%] self-start rounded-2xl rounded-bl-md bg-white px-3 py-2 text-[12.5px] leading-snug text-[#111] shadow-sm">
          <span className="font-semibold text-[#1F7A43]">Erledigt <Check size={12} strokeWidth={3} className="inline" /></span> – steht live auf deiner Mini-Website.
          <span className="mt-1 block text-right text-[10px] text-[#667781]">14:02</span>
        </div>
        <div className="max-w-[88%] self-end rounded-2xl rounded-br-md bg-[#DCF8C6] px-3 py-2 text-[12.5px] leading-snug text-[#111] shadow-sm">
          Perfekt, danke! 🙌
          <span className="mt-1 block text-right text-[10px] text-[#667781]">14:03 ✓✓</span>
        </div>
      </div>
    </div>
  );
}

function RobbieFigur({ text }) {
  const reduce = usePrefersReducedMotion();
  return (
    <div className="relative mx-auto w-max select-none" data-testid="mw-hero-robbie">
      <div className="relative z-10 ml-auto w-max max-w-[230px] rounded-2xl border-2 border-[#111] bg-white px-4 py-2.5 shadow-[4px_4px_0_#111]">
        <p className="text-sm font-semibold leading-snug text-[#111]">{text}</p>
        <span className="absolute -bottom-2 left-10 h-3.5 w-3.5 rotate-45 border-b-2 border-r-2 border-[#111] bg-white" aria-hidden="true" />
      </div>
      <motion.img
        src={ROBBIE} alt="Robbie, dein digitaler Mitarbeiter, winkt" width="320" height="320"
        className="relative z-10 mx-auto mt-2 w-44 drop-shadow-[0_20px_34px_rgba(0,0,0,0.25)] sm:w-56"
        animate={reduce ? {} : { y: [0, -10, 0], rotate: [0, -1.6, 0] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

export default function MiniWebsitePage() {
  return (
    <main className="bg-[#FBFAF5] text-[#111]" data-testid="mini-website-landing">
      <Seo title="Mini-Website – deine Seite in 2 Minuten | DIMEDIAL Studio" description="Deine eigene Mini-Website unter DIMEDIAL.studio/@name: Links, Galerie, Öffnungszeiten, Kundenkarte und Newsletter auf einer Seite. Die erste Seite ist für immer kostenlos. Echte Kundenseiten ansehen – z. B. Schuhhaus Tscheche, Denroy Cadeau und CDU Hövelhof." path="/mini-website" />

      {/* ───────── Hero ───────── */}
      <section className="relative overflow-hidden border-b-2 border-[#111] bg-[#FBFAF5]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(17,17,17,0.14) 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pt-24 pb-14 md:pt-28 md:pb-20 lg:grid-cols-[1.15fr_0.85fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-[#111] shadow-[3px_3px_0_#111]" data-testid="mw-hero-badge">
              <Star size={14} className="fill-[#111]" /> Eine Mini-Website ist für immer kostenlos
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="mw-hero-headline">
              Deine eigene<br />Seite –<br /><span className="bg-[#FDD105] px-2 shadow-[3px_3px_0_#111]">in 2 Minuten.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base font-medium leading-relaxed text-[#2b2b2b] sm:text-lg" data-testid="mw-hero-sub">
              Links, Öffnungszeiten, Galerie, Kundenkarte und Newsletter – alles auf einer Seite unter deiner Adresse <span className="whitespace-nowrap font-bold">DIMEDIAL.studio/@name</span>. Keine Vorlagen-Demos: unten siehst du <span className="font-bold">drei echte Kundenseiten</span>.
            </p>

            {/* Module benannt – oben */}
            <div className="mt-6" data-testid="mw-hero-module-chips">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#111]/60">Deine Module</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {MODULE.map(({ Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#111] bg-white px-3 py-1 text-[12px] font-bold text-[#111] shadow-[2px_2px_0_#111]" data-testid={`mw-chip-${label}`}>
                    <Icon size={13} /> {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={CTA} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#111] px-6 py-3 text-sm font-bold uppercase text-white shadow-[5px_5px_0_#FDD105] transition-transform hover:-translate-y-0.5" data-testid="mw-hero-cta">
                Kostenlos starten <ArrowRight size={16} />
              </Link>
              <a href="#echte-seiten" className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-6 py-3 text-sm font-bold uppercase transition-transform hover:-translate-y-0.5" data-testid="mw-hero-more">
                Echte Seiten ansehen
              </a>
            </div>
            <p className="mt-3 text-xs font-semibold text-[#111]/70">Erste Seite für immer gratis · keine Zahlungsdaten · in jedem Paket enthalten</p>
          </div>

          {/* Robbie */}
          <div className="relative flex items-center justify-center">
            <div aria-hidden="true" className="absolute h-60 w-60 rounded-full border-2 border-[#111] bg-[#FDD105] shadow-[10px_10px_0_#111] sm:h-72 sm:w-72" />
            <div className="relative">
              <RobbieFigur text="Sag mir einfach, was drauf soll – ich bau deine Seite." />
            </div>
          </div>
        </div>
      </section>

      {/* ───────── Echte Kundenseiten (Live) ───────── */}
      <section id="echte-seiten" className="scroll-mt-24 border-b-2 border-[#111] bg-white" data-testid="mw-echte-seiten">
        <div className="mx-auto max-w-6xl px-5 py-14 md:py-20">
          <Reveal className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-xs font-bold uppercase tracking-wide"><Zap size={14} /> Keine Platzhalter</span>
            <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold uppercase leading-tight sm:text-4xl" data-testid="mw-echte-title">Das sind echte Mini-Websites.</h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-[#5C574F]">Tippe eine Karte an, um die Original-Seite zu öffnen – jede im eigenen Design des Geschäfts.</p>
          </Reveal>
          <div className="mt-10 flex flex-wrap items-start justify-center gap-x-8 gap-y-12">
            {REAL_PAGES.map((p, i) => (
              <Reveal key={p.handle} delay={i * 0.1}>
                <LivePage page={p} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link to={CTA} className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#111] px-6 py-3 text-sm font-bold uppercase text-white transition-transform hover:-translate-y-0.5" data-testid="mw-echte-cta">
              Deine eigene Seite – kostenlos <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ───────── Für immer kostenlos – Banner ───────── */}
      <section className="border-b-2 border-[#111] bg-[#111]" data-testid="mw-paket-banner">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-5 py-8 text-center md:flex-row md:text-left">
          <div>
            <p className="font-display text-xl font-extrabold uppercase leading-tight text-white sm:text-2xl" data-testid="mw-paket-headline">
              Deine erste Mini-Website ist <span className="text-[#FDD105]">für immer kostenlos</span>.
            </p>
            <p className="mt-1 text-sm text-white/70">Ohne Zahlungsdaten, ohne Zeitlimit. Mehr Seiten gibt es im Paket: START 5 · PLUS 10 · TEAM 50.</p>
          </div>
          <Link to={CTA} className="shrink-0 rounded-full border-2 border-[#FDD105] bg-[#FDD105] px-6 py-3 text-sm font-bold uppercase text-[#111] transition-transform hover:-translate-y-0.5" data-testid="mw-paket-cta">
            Gratis Seite sichern
          </Link>
        </div>
      </section>

      {/* ───────── WhatsApp mit Robbie ───────── */}
      <section className="border-b-2 border-[#111] bg-white" data-testid="mw-whatsapp">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-20">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-3 py-1 text-xs font-bold uppercase tracking-wide"><Smartphone size={14} /> Kein Login, kein Editor</span>
            <h2 className="mt-5 font-display text-3xl font-extrabold uppercase leading-tight sm:text-4xl" data-testid="mw-whatsapp-title">Pflege sie vom Handy –<br />per WhatsApp mit Robbie.</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-[#444]">Neue Öffnungszeit, neuer Link, neues Foto? Schreib Robbie einfach eine Nachricht – er trägt es ein und deine Mini-Website ist sofort aktuell. So einfach wie einem Kollegen Bescheid geben.</p>
            <ul className="mt-5 space-y-2 text-sm font-medium text-[#222]">
              <li className="flex items-center gap-2"><Check size={16} className="text-[#1F7A43]" /> Änderungen in Sekunden live</li>
              <li className="flex items-center gap-2"><Check size={16} className="text-[#1F7A43]" /> Kein technisches Wissen nötig</li>
              <li className="flex items-center gap-2"><Check size={16} className="text-[#1F7A43]" /> Vom Sofa, Tresen oder unterwegs</li>
            </ul>
          </Reveal>
          <Reveal delay={0.1} className="flex justify-center">
            <RobbieChat />
          </Reveal>
        </div>
      </section>

      {/* ───────── Module ausführlich ───────── */}
      <section id="module" className="scroll-mt-24 border-b-2 border-[#111] bg-[#FBFAF5]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:py-20">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="mw-module-title">Alle Module<br />zum Antippen.</h2>
            <p className="mt-2 max-w-xl text-base text-[#5C574F]">Setz zusammen, was dein Geschäft braucht – jedes Modul mit einem Fingertipp eingefügt und sortiert.</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-5">
            {MODULE_DETAIL.map(({ Icon, title, text, live }, i) => (
              <Reveal key={title} delay={(i % 5) * 0.06}>
                <div className="group h-full rounded-2xl border-2 border-[#111] bg-white p-4 shadow-[4px_4px_0_#111] transition-transform hover:-translate-y-1" data-testid={`mw-module-${title}`}>
                  <span className="relative grid h-10 w-10 place-items-center rounded-xl bg-[#FDD105] text-[#111]">
                    <Icon size={19} />
                    {live && (
                      <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1F7A43] opacity-70" />
                        <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-white bg-[#1F7A43]" />
                      </span>
                    )}
                  </span>
                  <h3 className="mt-3 font-display text-sm font-bold uppercase leading-tight">{title}</h3>
                  <p className="mt-1 text-xs leading-snug text-[#666]">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Branchen ───────── */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-20" data-testid="mw-branchen">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="mw-branchen-title">Für jede Branche<br />das Richtige.</h2>
          <p className="mt-2 max-w-xl text-base text-[#5C574F]">Du wählst die Module – hier ein paar typische Beispiele, was drauf passt.</p>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {BRANCHEN.map(({ Icon, name, text }, i) => (
            <Reveal key={name} delay={(i % 2) * 0.08}>
              <div className="flex h-full items-start gap-4 rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[6px_6px_0_#111]" data-testid={`mw-branche-${name}`}>
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#111] text-[#FDD105]"><Icon size={24} /></span>
                <div><h3 className="font-display text-lg font-bold uppercase leading-tight">{name}</h3><p className="mt-1 text-sm leading-relaxed text-[#444]">{text}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ───────── Workflow ───────── */}
      <section className="border-y-2 border-[#111] bg-white">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-20">
          <div>
            <h2 className="font-display text-3xl font-extrabold uppercase sm:text-4xl" data-testid="mw-workflow-title">So einfach<br />geht's.</h2>
            <div className="mt-7 space-y-4">
              {STEPS.map(({ n, title, text }) => (
                <div key={n} className="flex gap-4" data-testid={`mw-step-${n}`}>
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-[#111] bg-[#FDD105] font-display text-lg font-extrabold">{n}</span>
                  <div><h3 className="font-display text-lg font-bold uppercase leading-tight">{title}</h3><p className="mt-0.5 text-sm text-[#555]">{text}</p></div>
                </div>
              ))}
            </div>
            <Link to={CTA} className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#111] px-6 py-3 text-sm font-bold uppercase text-white transition-transform hover:-translate-y-0.5" data-testid="mw-workflow-cta">Jetzt starten <ArrowRight size={16} /></Link>
          </div>
          <div className="flex justify-center">
            <LivePage page={REAL_PAGES[1]} />
          </div>
        </div>
      </section>

      {/* ───────── Alles hängt zusammen ───────── */}
      <section className="border-b-2 border-[#111] bg-[#FDD105]" data-testid="mw-connected">
        <div className="mx-auto max-w-5xl px-5 py-16 text-center md:py-20">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-white px-3 py-1 text-xs font-bold uppercase tracking-wide"><Zap size={14} /> Ein Konto</span>
            <h2 className="mt-5 font-display text-3xl font-extrabold uppercase leading-tight sm:text-4xl" data-testid="mw-connected-title">Alles hängt zusammen.</h2>
            <p className="mx-auto mt-3 max-w-xl text-base font-medium text-[#2b2b2b]">Wie bei Denroy Cadeau: Eine Newsletter-Anmeldung auf der Mini-Website landet direkt in der Liste. Kundenkarte, Tickets und Social Media laufen im selben Konto mit – kein Daten-Hin-und-Her.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-9 flex flex-col items-center gap-4">
              <div className="inline-flex items-center gap-2 rounded-2xl border-2 border-[#111] bg-[#111] px-5 py-3 text-sm font-bold uppercase text-[#FDD105] shadow-[5px_5px_0_rgba(0,0,0,0.25)]">
                <Smartphone size={18} /> Deine Mini-Website
              </div>
              <ArrowRight size={22} className="rotate-90 text-[#111]" />
              <div className="flex flex-wrap items-center justify-center gap-3">
                {VERKNUEPFT.map(({ Icon, label }) => (
                  <div key={label} className="inline-flex items-center gap-2 rounded-xl border-2 border-[#111] bg-white px-4 py-2.5 text-sm font-bold text-[#111] shadow-[4px_4px_0_#111]" data-testid={`mw-connected-${label}`}>
                    <Icon size={16} /> {label}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="border-b-2 border-[#111] bg-[#FBFAF5]" data-testid="mw-faq">
        <div className="mx-auto max-w-3xl px-5 py-14 md:py-16">
          <h2 className="font-display text-2xl font-extrabold uppercase sm:text-3xl" data-testid="mw-faq-title">Häufige Fragen</h2>
          <div className="mt-6 space-y-4">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="rounded-2xl border-2 border-[#111] bg-white p-5 shadow-[4px_4px_0_#111]" data-testid={`mw-faq-item-${q}`}>
                <h3 className="font-display text-base font-bold uppercase leading-tight sm:text-lg">{q}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#444]">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── Abschluss-CTA ───────── */}
      <section className="border-t-2 border-[#111] bg-[#111]">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:py-20">
          <h2 className="font-display text-3xl font-extrabold uppercase text-white sm:text-4xl lg:text-5xl" data-testid="mw-cta-headline">Deine Mini-Website.<br />Für immer kostenlos.</h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-white/75">Leg jetzt deine eigene Seite unter DIMEDIAL.studio/@name an – ohne Zahlungsdaten. Sichtbarkeit (Social, Google, Tickets) läuft im selben Konto mit.</p>
          <Link to={CTA} className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-[#111] bg-[#FDD105] px-8 py-4 text-base font-bold uppercase text-[#111] shadow-[6px_6px_0_rgba(253,209,5,0.3)] transition-transform hover:-translate-y-1" data-testid="mw-cta-button">Kostenlos starten <ArrowRight size={18} /></Link>
        </div>
      </section>
    </main>
  );
}
